// Dates, numbers and words shared by the admin screens (Memory, Feedback, Audit log). Dates are
// read in the person's zone (Preferences, else the company's: lib/time.ts, R10-B2), as times are.

import { lang, t } from "../../../i18n";
import {
  addDays as addCalendarDays,
  clock,
  dayKey,
  dayOfValue,
  daysBetween,
  formatDay,
  formatDayParts,
  zonedTime,
} from "../../../lib/time";

/** The locale the words follow: read at each call, since the language can change. */
function locale(): string {
  return lang() === "vi" ? "vi-VN" : "en-US";
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/** A local calendar day as YYYY-MM-DD (a date field's value, what the API's `from`/`to`
 *  take). */
export function isoDay(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** YYYY-MM-DD → that local day at midnight (never shifted by UTC). */
export function parseDay(day: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** A calendar day plus `n` days (negative goes back). */
export function addDays(day: string, n: number): string {
  return addCalendarDays(day, n);
}

/** The day of an instant in the person's zone, as YYYY-MM-DD ("" for none). */
export function dayOf(iso: string | null | undefined): string {
  return iso ? dayKey(iso) : "";
}

/** The end of a day in the person's zone, as an instant ("valid until Fri Oct 2" = 23:59:59
 *  that day there). */
export function endOfDay(day: string): string | null {
  return zonedTime(day, 23, 59, 59);
}

/** "Sep 21": a moment's day in the person's zone, or a calendar day as written. */
export function shortDate(d: Date | string): string {
  return formatDay(dayOfValue(d), { month: "short", day: "numeric" }, locale());
}

/** "Fri Oct 2" (the mockup's way of naming a day): a moment's day in the person's zone, or a
 *  calendar day ("2026-10-02", "2026-10-02T12:00:00") as written. */
export function dayName(d: Date | string): string {
  const day = dayOfValue(d);
  const weekday = formatDay(day, { weekday: "short" }, locale());
  // The month as it reads beside a day: "Oct"; in Vietnamese "thg 10", not the standalone
  // "Tháng 10".
  const month =
    formatDayParts(day, { month: "short", day: "numeric" }, locale()).find(
      (p) => p.type === "month",
    )?.value ?? "";
  return t("mem.dayName", { weekday, month, day: Number(day.slice(8, 10)) });
}

/** A list's time column: "today 10:43", "Mon 14:02" this week, else "Fri Sep 25". */
export function when(iso: string, now: Date = new Date()): string {
  const day = dayKey(iso);
  const current = dayKey(now);
  if (day === current) return t("mem.todayAt", { time: clock(iso) });
  const days = (now.getTime() - new Date(iso).getTime()) / 86_400_000;
  if (days > 0 && days < 6) {
    return `${formatDay(day, { weekday: "short" }, locale())} ${clock(iso)}`;
  }
  const s = dayName(iso);
  return day.slice(0, 4) === current.slice(0, 4) ? s : `${s}, ${day.slice(0, 4)}`;
}

/** A detail panel's time: "Mon Sep 28, 10:36". */
export function fullWhen(iso: string): string {
  return `${dayName(iso)}, ${clock(iso)}`;
}

/** A date range's button label (calendar days, as picked). */
export function rangeLabel(from?: string, to?: string): string {
  const a = from && parseDay(from) ? from : null;
  const b = to && parseDay(to) ? to : null;
  if (a && b) return a === b ? shortDate(a) : `${shortDate(a)} – ${shortDate(b)}`;
  if (a) return t("mem.since", { date: shortDate(a) });
  if (b) return t("mem.until", { date: shortDate(b) });
  return t("mem.anyTime");
}

/** Whole days from `now` until `iso`, by the days they fall on in the person's zone
 *  (negative when it's past). */
export function daysUntil(iso: string, now: Date = new Date()): number {
  return daysBetween(dayKey(now), dayKey(iso));
}

export function pct(x: number | null | undefined): string {
  return x === null || x === undefined ? "—" : `${Math.round(x * 100)}%`;
}

/** Spend per answer: cents below a dollar ("2.8¢", "31¢"), dollars above ("$1.20"). */
export function money(usd: number | null | undefined): string {
  if (usd === null || usd === undefined) return "—";
  if (usd >= 1) return `$${usd.toFixed(2)}`;
  const cents = usd * 100;
  if (cents === 0) return "0¢";
  if (cents < 0.1) return "<0.1¢";
  return `${cents < 10 ? cents.toFixed(1).replace(/\.0$/, "") : Math.round(cents)}¢`;
}

export function num(n: number): string {
  return n.toLocaleString(locale());
}

/** "1 answer", "4 answers" (English words; the admin screens' own counts use `tn`). */
export function plural(n: number, one: string, many = `${one}s`): string {
  return `${num(n)} ${n === 1 ? one : many}`;
}

/** Whitespace collapsed to single spaces: a fact is one line of the briefing. */
export function oneLine(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

/** Lowercase, unaccented, dashes: the server's `slug()` (memory/facts.py), for `topic:` keys. */
export function slug(value: string): string {
  return value
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .normalize("NFKD")
    .replace(/[^\x20-\x7e]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** The server's `topic_key()`: unaccented words, the last one singular ("Deploy Days" →
 *  "deploy day"). */
export function topicKey(label: string): string {
  const words = slug(label).split("-");
  const last = words[words.length - 1];
  if (last && last.length > 3 && last.endsWith("s") && !last.endsWith("ss")) {
    words[words.length - 1] = last.slice(0, -1);
  }
  return words.filter(Boolean).join(" ");
}
